import Image from "~components/common/Image";
import {
  usePaymentListViewModel,
  PaymentListProps,
} from "~components/checkout/financial/checkout.PaymentList";
import {
  CustomPaymentType,
  PaymentOption,
} from "~features/checkout/common/checkout.types";
import {
  getPaymentOptions,
  GetPaymentOptionsProps,
} from "~features/checkout/legacy/helpers/getPaymentOptions";
import { IntegrationType } from "~graphql/sdk.test-data";
import { GetPaymentOptionsDocument } from "~graphql/typed-document-nodes";
import { useQuery } from "~hooks/useQuery";
import { checkoutDebug } from "~lib/debug";

export const PAYMENT_PLAN_OPTION_ID = "stripe-payment-plan-dummy" as const;

type UseGetPaymentOptionsProps = GetPaymentOptionsProps &
  Omit<PaymentListProps, "paymentOptions" | "totals"> & {
    totals: GetPaymentOptionsProps["totals"];
    orderId: string;
    selectedPartPaymentId?: string;
  };

/**
 * Hook to fetch and manage payment options for checkout
 * Handles payment option retrieval, transformation, and selection
 */
export function useGetPaymentOptions(props: UseGetPaymentOptionsProps) {
  const {
    isAdmin,
    userRoles,
    isPOSUser,
    posPaymentOptions,
    totals,
    gateways,
    pointName,
    canSpendPoints,
    canCreateHoldOrder,
    hasPaidInFullWithCredits,
    isBackOfficeOrder,
    paymentType,
    selectedId,
    onClick,
    showHiddenFees,
    totalForBNPL,
    eventId,
    membershipId,
    selectedPartPaymentId,
    orderId,
  } = props;

  // Fetch part-payment options from backend
  const { data: paymentOptionsResult } = useQuery(
    orderId ? GetPaymentOptionsDocument : null,
    {
      id: orderId,
    }
  );

  // Get base payment options using legacy helper
  const paymentOptions = getPaymentOptions({
    isAdmin,
    userRoles,
    isPOSUser,
    posPaymentOptions,
    totals,
    gateways,
    pointName,
    canSpendPoints,
    canCreateHoldOrder,
    hasPaidInFullWithCredits,
    isBackOfficeOrder,
    backendPaymentOptions:
      paymentOptionsResult?.getPaymentOptions?.options ?? [],
  });

  // Transform payment options to include payment plan structure
  const modifiedPaymentOptions = addPaymentPlanOptions(paymentOptions);

  // Generate payment list with view model
  const {
    payments,
    events: { handleItemClick },
  } = usePaymentListViewModel({
    paymentType,
    paymentOptions: modifiedPaymentOptions,
    totals,
    selectedId,
    onClick,
    showHiddenFees,
    totalForBNPL: totalForBNPL ?? totals.orderTotal,
    eventId,
    membershipId,
  });

  // Update payment options display (wording, images, etc.)
  const updatedPaymentOptions = payments.map(updatePaymentOptionDisplay);

  const regularPaymentOptions = updatedPaymentOptions
    .filter((p) => !p.paymentPlanMetadata)
    .sort((a, b) => ((a.order ?? 0) > (b.order ?? 0) ? 1 : -1));

  // Filter payment plan options
  const partPaymentOptions = updatedPaymentOptions
    .filter((p) => !!p.paymentPlanMetadata)
    .sort((a, b) => ((a.order ?? 0) > (b.order ?? 0) ? 1 : -1));

  const selectedPayment = regularPaymentOptions.find(
    (p) => p.value === selectedId
  );
  const selectedPartPayment = partPaymentOptions.find(
    (p) => p.value === selectedPartPaymentId
  );

  checkoutDebug("useGetPaymentOptions", {
    regularPaymentOptions,
    partPaymentOptions,
    selectedPayment,
  });

  return {
    paymentOptions: regularPaymentOptions,
    partPaymentOptions,
    paymentType,
    selectedPayment,
    selectedPartPayment,
    showPartPaymentOptions:
      selectedPayment?.value === PAYMENT_PLAN_OPTION_ID &&
      partPaymentOptions?.length > 0,
    handleItemClick,
    handlePaymentSelect: (value: string) =>
      handleItemClick(value, selectedPartPayment?.value),
    handlePartPaymentSelect: (partPaymentValue: string) =>
      handleItemClick(selectedPayment?.value, partPaymentValue),
  };
}

/**
 * Adds payment plan options for Stripe payment methods
 * Splits "Pay in full" into two options: one for immediate payment and a dummy for payment plans
 */
function addPaymentPlanOptions(paymentOptions: PaymentOption[]) {
  return paymentOptions.reduce((acc, option) => {
    if (
      option.type === IntegrationType.PaymentStripe &&
      option.label === "Pay in full"
    ) {
      // Add "Pay in full" option with payment plan metadata
      // Order: 0 ensures it appears first in the payment plan options list
      acc.push({
        ...option,
        order: 0,
        description: "Pay the full amount now.",
        // @ts-expect-error Make this payment item appear with the payment plan options section
        paymentPlanMetadata: {},
      });

      // Add a dummy option for selecting payment plans labeled as "Credit or debit card"
      acc.push({
        ...option,
        id: PAYMENT_PLAN_OPTION_ID,
        value: PAYMENT_PLAN_OPTION_ID,
        label: "Credit or debit card",
        type: IntegrationType.PaymentStripe,
        // Make this item appear with the payments options
        paymentPlanMetadata: undefined,
      });
    } else {
      acc.push(option);
    }

    return acc;
  }, [] as PaymentOption[]);
}

/**
 * Updates the display properties of payment options
 * Transforms legacy wording and styling to match new checkout design
 */
function updatePaymentOptionDisplay(
  payment: ReturnType<typeof usePaymentListViewModel>["payments"][number]
) {
  if (payment.type === CustomPaymentType.OnHold) {
    return {
      ...payment,
      title: "Create hold order",
      subtitle: "Hold these sheets and issue an invoice for payment.",
    };
  }

  if (payment.type === CustomPaymentType.Paid) {
    return {
      ...payment,
      subtitle:
        "Complete this order offline and mark as paid by either cash, eftpos, invoice or other means.",
    };
  }

  if (payment.paymentPlanMetadata) {
    return {
      ...payment,
      image: undefined,
      icon: undefined,
    };
  }

  if (
    payment.type === IntegrationType.PaymentStripe &&
    payment.title !== "Afterpay"
  ) {
    return {
      ...payment,
      subtitle: (
        <>
          <Image
            src={"/static/logos/visa-mastercard-amex.svg"}
            alt={payment.title}
            width="102px"
            height="12px"
            mt={"1/2"}
          />
        </>
      ),
    };
  }

  // Taken from PaymentList component: src/components/checkout/financial/checkout.PaymentList.tsx
  //! Temp hack: Afterpay won't like seeing the changing price in the subtitle (as total is based off selected payment option).
  // Solution for now is to hide the subtitle for Afterpay unless it's selected.
  if (
    (payment.type === IntegrationType.PaymentAfterpay ||
      payment.title === "Afterpay") &&
    !payment.isActive
  ) {
    return {
      ...payment,
      // Note: subtitle intentionally left undefined
    };
  }

  return payment;
}
